import { useMutation, useQueryClient } from '@tanstack/react-query'

import { apiScoping } from '@/lib/api/scoping'
import { assessmentKeys } from '@/features/assessments/api/query-keys'
import { buildHubspotExternalReferences } from '@/features/assessments/utils/build-hubspot-external-references'
import { buildHubspotPlantExternalReferences } from '@/features/assessments/utils/build-hubspot-plant-external-references'

export type CreateAssessmentInput = {
  plantName: string
  country: string
  dealOwnerUserID?: string
  salesEngineerUserID?: string
  isAtex?: boolean
  // Flag off (today): the deal reference lives on the assessment.
  hubspotDealId?: string | null
  // Flag on (multiPhase): the plant reference lives on the assessment and the
  // deal moves to installation phases. When present, only the hubspotPlant
  // entry is sent.
  hubspotPlantId?: string | null
}

type CreateAssessmentResponse = {
  id: string
}

async function createAssessment(input: CreateAssessmentInput): Promise<CreateAssessmentResponse> {
  const { hubspotDealId, hubspotPlantId, ...rest } = input
  const body = {
    ...rest,
    status: 'pending' as const,
    externalReferences:
      hubspotPlantId !== undefined
        ? buildHubspotPlantExternalReferences(hubspotPlantId)
        : buildHubspotExternalReferences(hubspotDealId)
  }
  const res = await apiScoping.post<CreateAssessmentResponse>('/api/v1/plant-assessment', body)
  return res.data
}

export function useCreateAssessment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createAssessment,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: assessmentKeys.lists() })
    }
  })
}
